import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { createConsultantRequest, sendChat } from "../lib/api";
import { Headset, Send, Loader2 } from "lucide-react";

const GREETING =
  "Namaste! I'm your ElderCare24 consultant. Tell me a little about your parents in Jaipur and how we can help — I'm right here.";

export const ConsultantNow = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("intake"); // intake | chat
  const [received, setReceived] = useState(false); // green light on FAB
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [starting, setStarting] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const startChat = async () => {
    if (!form.name.trim()) {
      toast.error("Please tell us your name to begin.");
      return;
    }
    setStarting(true);
    try {
      await createConsultantRequest({
        name: form.name,
        phone: form.phone || "not provided",
        note: form.note,
      });
    } catch (e) {
      /* still open chat even if logging the request fails */
    }
    const sid = (crypto?.randomUUID?.() || String(Date.now())) + "";
    setSessionId(sid);
    setReceived(true); // green light blinks now that the request is received
    setMessages([{ role: "assistant", text: GREETING }]);
    setStage("chat");
    setStarting(false);
    toast.success("Request received — a consultant is online now.");
  };

  const requestCallback = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number for the callback.");
      return;
    }
    setStarting(true);
    try {
      await createConsultantRequest({
        name: form.name,
        phone: form.phone,
        note: form.note || "Callback requested",
      });
      setReceived(true);
      setStage("confirmed");
      toast.success("Callback request recorded.");
    } catch (e) {
      toast.error("Could not record your request. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setTyping(true);
    try {
      const { reply } = await sendChat(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sorry, I lost connection for a second. Please try again." },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const close = (v) => {
    setOpen(v);
    if (!v)
      setTimeout(() => {
        setStage("intake");
        setMessages([]);
        setInput("");
        setForm({ name: "", phone: "", note: "" });
      }, 300);
  };

  return (
    <>
      <motion.button
        data-testid="consultant-now-fab"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-cream pl-4 pr-5 py-3 shadow-xl"
      >
        <span className="relative flex h-3 w-3" data-testid="fab-status-light">
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${
              received ? "bg-green-500" : "bg-brand-gold"
            }`}
          />
          <span
            className={`relative inline-flex rounded-full h-3 w-3 ${
              received ? "bg-green-500" : "bg-brand-gold"
            }`}
          />
        </span>
        <Headset size={18} />
        <span className="text-sm font-semibold">Talk to a consultant now</span>
      </motion.button>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent
          data-testid="consultant-dialog"
          className="max-w-md bg-brand-cream border-border p-0 overflow-hidden"
        >
          {stage === "intake" ? (
            <div className="p-6">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light text-brand-ink flex items-center gap-2">
                  <Headset className="text-brand" size={22} /> Talk to a consultant now
                </DialogTitle>
                <DialogDescription className="text-brand-ink/60">
                  No waiting. Share your details and start chatting with our consultant right away.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-brand-ink/80 text-sm mb-1.5 block">Your name</Label>
                  <Input
                    data-testid="consultant-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label className="text-brand-ink/80 text-sm mb-1.5 block">Phone (optional)</Label>
                  <Input
                    data-testid="consultant-phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="So we can follow up"
                  />
                </div>
                <div>
                  <Label className="text-brand-ink/80 text-sm mb-1.5 block">How can we help? (optional)</Label>
                  <Textarea
                    data-testid="consultant-note"
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    rows={2}
                    placeholder="Briefly tell us what you need"
                  />
                </div>
                <Button
                  data-testid="consultant-start-chat"
                  onClick={startChat}
                  disabled={starting}
                  className="w-full rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white h-12"
                >
                  {starting ? "Please wait..." : "Start chatting now"}
                </Button>

                <div className="relative text-center py-1">
                  <span className="text-xs text-brand-ink/40 bg-brand-cream px-3">or</span>
                </div>

                <Button
                  data-testid="consultant-request-callback"
                  onClick={requestCallback}
                  disabled={starting}
                  variant="outline"
                  className="w-full rounded-full border-brand text-brand hover:bg-brand/5 h-12"
                >
                  Request a callback
                </Button>
                <p className="text-xs text-brand-ink/50 text-center">
                  We'll record your request and our team will call you back.
                </p>
              </div>
            </div>
          ) : stage === "confirmed" ? (
            <div className="p-8 text-center" data-testid="callback-confirmed">
              <div className="mx-auto h-16 w-16 rounded-full bg-green-500/15 flex items-center justify-center mb-5">
                <span className="relative flex h-4 w-4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500" />
                </span>
              </div>
              <h3 className="font-serif text-2xl text-brand-ink mb-2">Callback recorded</h3>
              <p className="text-brand-ink/70 text-sm mb-6">
                Thank you, {form.name.split(" ")[0] || "there"}. Your request has been recorded and
                our team will call you back shortly{form.phone ? ` on ${form.phone}` : ""}.
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  data-testid="confirmed-chat-btn"
                  variant="outline"
                  onClick={() => {
                    setSessionId((crypto?.randomUUID?.() || String(Date.now())) + "");
                    setMessages([{ role: "assistant", text: GREETING }]);
                    setStage("chat");
                  }}
                  className="rounded-full"
                >
                  Chat while you wait
                </Button>
                <Button
                  data-testid="confirmed-done-btn"
                  onClick={() => close(false)}
                  className="rounded-full bg-brand hover:bg-brand-hover text-white"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-[70vh] max-h-[560px]">
              {/* chat header with green online light */}
              <div className="flex items-center gap-3 px-5 py-4 bg-brand-ink text-brand-cream">
                <span className="relative flex h-3 w-3" data-testid="chat-online-light">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                </span>
                <div>
                  <p className="font-semibold text-sm leading-none">Consultant online</p>
                  <p className="text-xs text-brand-cream/60 mt-1">ElderCare24 · Jaipur</p>
                </div>
              </div>

              {/* messages */}
              <div
                ref={scrollRef}
                data-testid="chat-messages"
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-cream"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-brand text-white rounded-br-sm"
                          : "bg-white text-brand-ink border border-border rounded-bl-sm"
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                {typing && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3">
                      <Loader2 className="animate-spin text-brand/60" size={16} />
                    </div>
                  </div>
                )}
              </div>

              {/* input */}
              <div className="p-3 border-t border-border bg-white flex items-center gap-2">
                <Input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type your message..."
                  className="rounded-full"
                />
                <Button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={typing || !input.trim()}
                  size="icon"
                  className="rounded-full bg-brand hover:bg-brand-hover text-white shrink-0"
                >
                  <Send size={16} />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
